// Version: 1.3
// v1.3: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
import React, { useEffect, useState, useCallback } from 'react';
import { authFetch } from './auth';
import AssetHub from './AssetHub';

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

const RAG_COLOURS = {
  expired: '#ff2d55',
  red:     '#ff2d55',
  amber:   '#e6b800',
  yellow:  '#e6b800',
  green:   '#30d158',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
}

function fmtCost(cost, currency) {
  if (!cost) return null;
  const sym = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '£';
  return `${sym}${parseFloat(cost).toFixed(2)}`;
}

// ── Expiring Soon Panel ───────────────────────────────────────────────────────

function ExpiringBand({ label, days, accent, items }) {
  const [expanded, setExpanded] = useState(true);
  const isMobile = useIsMobile();
  if (!items.length) return null;
  return (
    <div style={{
      border:`1px solid ${accent}40`,borderRadius:8,overflow:'hidden',
    }}>
      <div
        onClick={()=>setExpanded(e=>!e)}
        style={{
          display:'flex',alignItems:'center',justifyContent:'space-between',
          padding:'10px 14px',background:`${accent}10`,cursor:'pointer',
        }}
      >
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <span style={{
            display:'inline-flex',alignItems:'center',justifyContent:'center',
            width:26,height:26,borderRadius:'50%',background:accent,
            color:'#000',fontWeight:800,fontSize:11,flexShrink:0,
          }}>{items.length}</span>
          <span style={{fontWeight:700,fontSize:13,color:accent}}>{label}</span>
        </div>
        <span style={{fontSize:11,color:'var(--text-muted)'}}>{expanded?'▲':'▼'}</span>
      </div>
      {expanded && (
        <div>
          {items.map((a,i) => (
            <div key={a.id} style={{
              display:'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 140px 110px 100px',
              gap:8,alignItems: isMobile ? 'start' : 'center',
              padding:'9px 14px',
              borderTop: i===0 ? `1px solid ${accent}20` : '1px solid var(--border)',
              fontSize:12,
            }}>
              <div>
                <div style={{fontWeight:600}}>{a.name}</div>
                <div style={{color:'var(--text-muted)',fontSize:11,marginTop:1}}>
                  {a.client_display_name}
                  {a.vendor ? ` · ${a.vendor}` : ''}
                </div>
              </div>
              <div>
                <div style={{color:accent,fontWeight:600,fontSize:12}}>
                  {a.days_until_expiry < 0
                    ? 'Expired'
                    : a.days_until_expiry === 0
                    ? 'Today'
                    : `${a.days_until_expiry} days`}
                </div>
                <div style={{color:'var(--text-muted)',fontSize:11}}>{fmtDate(a.expiry_date)}</div>
              </div>
              <div style={{color:'var(--text-muted)'}}>
                {fmtCost(a.cost,a.currency) || '—'}
                {a.auto_renew && <span style={{marginLeft:4,color:'var(--accent)',fontSize:10}}>↻ auto</span>}
              </div>
              <div>
                <span style={{
                  padding:'2px 7px',borderRadius:4,fontSize:10,fontWeight:700,
                  background: a.visible_to_client ? '#30d15820' : 'var(--border)',
                  color: a.visible_to_client ? '#30d158' : 'var(--text-muted)',
                }}>{a.visible_to_client ? 'Visible' : 'Private'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExpiringSoonPanel({ assets }) {
  // Three bands: expired/30 days, 31-60 days, 61-90 days
  const withExpiry = assets.filter(a => a.expiry_date);

  const band30  = withExpiry.filter(a => a.days_until_expiry != null && a.days_until_expiry <= 30)
    .sort((a,b)=>a.days_until_expiry - b.days_until_expiry);
  const band60  = withExpiry.filter(a => a.days_until_expiry != null && a.days_until_expiry > 30 && a.days_until_expiry <= 60)
    .sort((a,b)=>a.days_until_expiry - b.days_until_expiry);
  const band90  = withExpiry.filter(a => a.days_until_expiry != null && a.days_until_expiry > 60 && a.days_until_expiry <= 90)
    .sort((a,b)=>a.days_until_expiry - b.days_until_expiry);

  const total = band30.length + band60.length + band90.length;
  if (total === 0) return null;

  return (
    <div style={{marginBottom:28}}>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
        <div style={{fontWeight:700,fontSize:15}}>Expiring Soon</div>
        <span style={{
          padding:'2px 8px',borderRadius:10,fontSize:11,fontWeight:700,
          background:'#ff2d5520',color:'#ff2d55',
        }}>{total} item{total!==1?'s':''} need attention</span>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        <ExpiringBand label="Within 30 days — action required"  days={30} accent="#ff2d55" items={band30}/>
        <ExpiringBand label="Within 60 days — review soon"      days={60} accent="#e6b800" items={band60}/>
        <ExpiringBand label="Within 90 days — on the horizon"   days={90} accent="#30d158" items={band90}/>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function AssetRegister() {
  const [assets,  setAssets]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [view,    setView]    = useState('register'); // 'register' | 'expiring'

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res  = await authFetch('/api/assets/global');
      const data = await res.json();
      setAssets(data.assets || []);
    } catch(e) { setError(String(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const expiringSoon = assets.filter(a =>
    a.expiry_date && a.days_until_expiry != null && a.days_until_expiry <= 90
  );

  return (
    <div style={{padding:'0 0 40px'}}>
      {/* Page header */}
      <div style={{marginBottom:20}}>
        <div style={{fontSize:22,fontWeight:800,letterSpacing:'-0.02em'}}>Asset Register</div>
        <div style={{fontSize:13,color:'var(--text-muted)',marginTop:4}}>
          All assets across all clients — devices, licences, contracts, domains and more
        </div>
      </div>

      {/* View toggle + summary */}
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20,flexWrap:'wrap'}}>
        <div className="segmented">
          {[
            { id:'register', label:'Full Register' },
            { id:'expiring', label:`Expiring Soon${expiringSoon.length ? ` (${expiringSoon.length})` : ''}` },
          ].map(v => (
            <button className="btn" aria-pressed={view===v.id} key={v.id} onClick={()=>setView(v.id)}>{v.label}</button>
          ))}
        </div>

        {!loading && (
          <div style={{fontSize:12,color:'var(--text-muted)'}}>
            {assets.length} total asset{assets.length!==1?'s':''}
            {expiringSoon.length > 0 && (
              <span style={{marginLeft:8,color:'#ff2d55',fontWeight:600}}>
                · {expiringSoon.filter(a=>a.days_until_expiry<=30).length} expiring within 30 days
              </span>
            )}
          </div>
        )}
      </div>

      {loading && <div style={{padding:24,color:'var(--text-muted)',fontSize:13}}>Loading assets…</div>}
      {error   && <div style={{padding:24,color:'#ff2d55',fontSize:13}}>{error}</div>}

      {!loading && !error && (
        <>
          {view === 'expiring' && <ExpiringSoonPanel assets={assets}/>}
          {view === 'register' && (
            <>
              {/* Inline expiring soon summary if any */}
              {expiringSoon.filter(a=>a.days_until_expiry<=30).length > 0 && (
                <div style={{
                  display:'flex',alignItems:'center',gap:10,
                  padding:'10px 14px',background:'#ff2d5510',
                  border:'1px solid #ff2d5530',borderRadius:8,marginBottom:16,
                  cursor:'pointer',
                }} onClick={()=>setView('expiring')}>
                  <span style={{fontSize:18}}>⚠️</span>
                  <span style={{fontSize:13,fontWeight:600,color:'#ff2d55'}}>
                    {expiringSoon.filter(a=>a.days_until_expiry<=30).length} asset{expiringSoon.filter(a=>a.days_until_expiry<=30).length!==1?'s':''} expiring within 30 days
                  </span>
                  <span style={{marginLeft:'auto',fontSize:12,color:'var(--text-muted)'}}>View →</span>
                </div>
              )}
              <AssetHub slug={null} isAdmin={true} showClient={true}/>
            </>
          )}
        </>
      )}
    </div>
  );
}
